import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, CircleHelp, X } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import { Button, ConfirmDialog } from '@/components/ui';
import { useDesignEntry } from '@/components/designs/DesignEntryChoice';
import { embedLayout } from '@/lib/embed-layout';
import { cn } from '@/lib/utils';
import {
  BULK_STEPS,
  isBulkStepComplete,
  useBulkCreationStore,
} from '@/stores/bulkCreationStore';

/** The primary action of step 03, owned by the page's submission. */
export interface BulkCreateAction {
  label: string;
  disabled: boolean;
  loading: boolean;
  loadingLabel: string;
  onClick: () => void;
}

export interface BulkFlowHeaderProps {
  /** CTA shown on step 03; steps 01 and 02 show "Weiter". */
  createAction: BulkCreateAction;
  /** A request is out: navigation waits until it settled. */
  busy?: boolean;
  className?: string;
}

/**
 * Flow header of Bulk Creation (R2–R4): back, the numbered step pills, help,
 * close and the primary CTA. The pills switch freely; an incomplete step
 * only locks the CTA. Closing throws the whole flow away and asks first once
 * rows exist, since uploads and titles would be lost.
 */
export function BulkFlowHeader({
  createAction,
  busy = false,
  className,
}: BulkFlowHeaderProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { startGuided } = useDesignEntry();
  // Host-framed (Shopify admin): no design overview to return to; the flow
  // was opened from the guided entry, so that is where leaving goes.
  const { hostChrome } = embedLayout();
  const helpId = useId();
  const helpRef = useRef<HTMLDivElement>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);

  const { currentStep, reference, activeProductIds, rows } =
    useBulkCreationStore(
      useShallow((state) => ({
        currentStep: state.currentStep,
        reference: state.reference,
        activeProductIds: state.activeProductIds,
        rows: state.rows,
      }))
    );
  const goToStep = useBulkCreationStore((state) => state.goToStep);
  const goToNextStep = useBulkCreationStore((state) => state.goToNextStep);
  const goToPreviousStep = useBulkCreationStore(
    (state) => state.goToPreviousStep
  );

  const completion = { reference, activeProductIds, rows };
  const currentIndex = BULK_STEPS.indexOf(currentStep);

  useEffect(() => {
    if (!helpOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setHelpOpen(false);
    };
    const onPointerDown = (event: PointerEvent) => {
      if (!helpRef.current?.contains(event.target as Node)) setHelpOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [helpOpen]);

  const leaveFlow = () => {
    useBulkCreationStore.getState().reset();
    if (hostChrome) startGuided();
    else navigate('/designs');
  };

  const requestClose = () => {
    if (rows.length > 0) setConfirmClose(true);
    else leaveFlow();
  };

  const handleBack = () => {
    if (currentIndex === 0) requestClose();
    else goToPreviousStep();
  };

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-3 rounded-card bg-paper-2 px-3 py-2.5 ring-1 ring-line',
        className
      )}
    >
      <Button
        variant="ghost"
        size="sm"
        onClick={handleBack}
        disabled={busy}
        className="text-ink-2"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        {t('seller.bulk.header.back')}
      </Button>

      <nav
        aria-label={t('seller.bulk.steps.label')}
        className="flex flex-1 flex-wrap items-center justify-center gap-1.5"
      >
        <ol className="flex flex-wrap items-center gap-1.5">
          {BULK_STEPS.map((step, index) => {
            const isCurrent = step === currentStep;
            // Step 03 is only "done" once the run started, which ends the flow.
            const isDone =
              step !== 'create' && isBulkStepComplete(step, completion);
            const number = String(index + 1).padStart(2, '0');
            return (
              <li key={step}>
                <button
                  type="button"
                  onClick={() => goToStep(step)}
                  disabled={busy}
                  aria-current={isCurrent ? 'step' : undefined}
                  className={cn(
                    'flex items-center gap-1.5 rounded-pill px-3 py-1.5 text-[13px] font-medium transition-colors',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                    'disabled:cursor-not-allowed disabled:opacity-60',
                    isCurrent
                      ? 'bg-ink text-paper'
                      : 'bg-paper text-ink-2 ring-1 ring-inset ring-line hover:text-ink'
                  )}
                >
                  <span className="font-mono text-[12px]">{number}</span>{' '}
                  <span>{t(`seller.bulk.steps.${step}`)}</span>
                  {isDone && (
                    <>
                      <Check
                        className={cn(
                          'h-3.5 w-3.5',
                          isCurrent ? 'text-paper' : 'text-success'
                        )}
                        aria-hidden="true"
                      />{' '}
                      <span className="sr-only">
                        {t('seller.bulk.steps.completed')}
                      </span>
                    </>
                  )}
                </button>
              </li>
            );
          })}
        </ol>
      </nav>

      <div className="flex items-center gap-2">
        <div ref={helpRef} className="relative">
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={t('seller.bulk.header.help')}
            aria-expanded={helpOpen}
            aria-controls={helpId}
            onClick={() => setHelpOpen((open) => !open)}
          >
            <CircleHelp className="h-4 w-4" aria-hidden="true" />
          </Button>
          {helpOpen && (
            <div
              id={helpId}
              role="note"
              className="absolute right-0 top-full z-30 mt-2 w-72 rounded-card bg-ink px-3.5 py-3 text-[13px] leading-snug text-paper ring-1 ring-line"
            >
              {t(
                hostChrome
                  ? 'seller.bulk.header.helpTextHost'
                  : 'seller.bulk.header.helpText'
              )}
            </div>
          )}
        </div>

        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={t('seller.bulk.header.close')}
          onClick={requestClose}
          disabled={busy}
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </Button>

        {currentStep === 'create' ? (
          <Button
            onClick={createAction.onClick}
            disabled={createAction.disabled}
            loading={createAction.loading}
            loadingLabel={createAction.loadingLabel}
            data-testid="bulk-flow-cta"
          >
            {createAction.label}
          </Button>
        ) : (
          <Button
            onClick={goToNextStep}
            disabled={busy || !isBulkStepComplete(currentStep, completion)}
            data-testid="bulk-flow-cta"
          >
            {t('seller.bulk.header.next')}
          </Button>
        )}
      </div>

      <ConfirmDialog
        open={confirmClose}
        onOpenChange={setConfirmClose}
        title={t('seller.bulk.closeConfirm.title')}
        description={t('seller.bulk.closeConfirm.text')}
        confirmLabel={t('seller.bulk.closeConfirm.confirm')}
        cancelLabel={t('seller.bulk.closeConfirm.cancel')}
        destructive
        onConfirm={() => {
          setConfirmClose(false);
          leaveFlow();
        }}
      />
    </div>
  );
}
